import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    useListContext,
    useRefresh,
    useNotify,
    useUnselectAll,
    useDataProvider,
} from 'react-admin';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloudIcon from '@mui/icons-material/Cloud';

const ADMIN_APP_GUID = '13b312b5-367c-4c74-bb77-bdb34df4e721';

const BulkChangeEnvsButton = () => {
    const { selectedIds } = useListContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const unselectAll = useUnselectAll('user');
    const dataProvider = useDataProvider();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [envOptions, setEnvOptions] = useState<
        { id: string; item_title: string }[]
    >([]);
    const [selectedEnvs, setSelectedEnvs] = useState<string[]>([]);

    useEffect(() => {
        dataProvider
            .getAny('extralist/userapplication/envs', '')
            .then(({ data }) => setEnvOptions(data))
            .catch(() => {});
    }, [dataProvider]);

    const handleOpen = () => {
        setSelectedEnvs([]);
        setDialogOpen(true);
    };

    const handleEnvToggle = (envId: string) => {
        setSelectedEnvs(prev =>
            prev.includes(envId)
                ? prev.filter(e => e !== envId)
                : [...prev, envId]
        );
    };

    const handleConfirm = async () => {
        setLoading(true);

        try {
            let successCount = 0;
            let errorCount = 0;

            for (const userId of selectedIds) {
                try {
                    const { data: uaList } = await dataProvider.getList(
                        'userapplication',
                        {
                            filter: { user_guid: userId },
                            pagination: { page: 1, perPage: 100 },
                            sort: { field: 'id', order: 'ASC' },
                        }
                    );

                    const ua = uaList.find(
                        (item: any) => item.application_guid === ADMIN_APP_GUID
                    );

                    if (ua) {
                        await dataProvider.update('userapplication', {
                            id: ua.guid,
                            data: {
                                ...ua,
                                public: {
                                    ...ua.public,
                                    envs: selectedEnvs,
                                },
                            },
                            previousData: ua,
                        });
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch {
                    errorCount++;
                }
            }

            const envLabels = selectedEnvs
                .map(e => envOptions.find(o => o.id === e)?.item_title || e)
                .join(', ');

            if (successCount > 0) {
                notify(
                    `${successCount} käyttäjän ympäristöt vaihdettu: ${envLabels || 'pääsyä ei rajattu'}`,
                    { type: 'success' }
                );
            }
            if (errorCount > 0) {
                notify(
                    `${errorCount} käyttäjän ympäristöjen vaihto epäonnistui`,
                    { type: 'warning' }
                );
            }
            refresh();
            unselectAll();
        } catch {
            notify('Virhe ympäristöjä vaihdettaessa', { type: 'error' });
        } finally {
            setLoading(false);
            setDialogOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                disabled={loading || envOptions.length === 0}
                color="primary"
                size="small"
            >
                <CloudIcon sx={{ mr: 0.5 }} fontSize="small" />
                Vaihda ympäristöt
            </Button>
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Vaihda {selectedIds.length} valitun käyttäjän ympäristöt
                </DialogTitle>
                <DialogContent>
                    <FormGroup sx={{ mt: 1 }}>
                        {envOptions.map(env => (
                            <FormControlLabel
                                key={env.id}
                                control={
                                    <Checkbox
                                        checked={selectedEnvs.includes(env.id)}
                                        onChange={() => handleEnvToggle(env.id)}
                                    />
                                }
                                label={env.item_title}
                            />
                        ))}
                    </FormGroup>
                    {selectedEnvs.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Pääsyä ei rajata mihinkään ympäristöön
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        disabled={loading}
                    >
                        Peruuta
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={loading}
                        variant="contained"
                        startIcon={
                            loading ? <CircularProgress size={16} /> : undefined
                        }
                    >
                        {loading ? 'Päivitetään...' : 'Tallenna'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BulkChangeEnvsButton;
